import type { HitSpec, ProjectileSpec, SkillAction, SkillDef, StatusApply } from '../../sim/skillTypes';
import type { Shape } from '../../sim/shapes';
import type { Element, SuperArmor } from '../../sim/types';
import { announce, area, circle, cone, counterWindow, hit, hits, move, proj, rect, skill, stage } from '../classes/kit';

/**
 * Builders for monster attacks. Monster skills carry a `range:lo-hi` tag the
 * AI uses to decide when an attack is usable. Wind-ups are deliberately
 * readable (0.4–0.7 s) because most attacks have no ground marker.
 */

export function mMelee(id: string, name: string, o: { shape: Shape; coef: number; windup?: number; cd?: number; range?: [number, number]; anim?: string; reaction?: HitSpec['reaction']; status?: HitSpec['status']; recover?: number }): SkillDef {
  const w = o.windup ?? 0.45;
  return skill({
    id, name, description: '', castType: 'normal', cooldown: o.cd ?? 2.5, mana: 0, kind: 'monster',
    tags: [`range:${(o.range ?? [0, 2.4]).join('-')}`],
    stages: [stage(o.anim ?? 'Attack', w + (o.recover ?? 0.45), [hit(w, o.shape, o.coef, { reaction: o.reaction ?? { kind: 'flinch' }, ...(o.status ? { status: o.status } : {}) })])],
  });
}

export function mLunge(id: string, name: string, o: { distance: number; coef: number; windup?: number; cd?: number; range?: [number, number]; anim?: string; width?: number; reaction?: HitSpec['reaction'] }): SkillDef {
  const w = o.windup ?? 0.5;
  return skill({
    id, name, description: '', castType: 'normal', cooldown: o.cd ?? 6, mana: 0, kind: 'monster',
    tags: [`range:${(o.range ?? [2, o.distance + 1]).join('-')}`],
    stages: [
      stage(o.anim ?? 'Run', w + 0.5, [
        area(0, { shape: { kind: 'rect', length: o.distance + 1, width: o.width ?? 1.6 }, delay: w, telegraph: 'red', hit: { shape: { kind: 'rect', length: 0.1, width: 0.1 }, coef: 0 } }),
        move(w, o.distance, 0.3),
        hit(w + 0.1, { kind: 'rect', length: 1.6, width: o.width ?? 1.6 }, o.coef, { reaction: o.reaction ?? { kind: 'knockback', power: 2 } }),
      ]),
    ],
  });
}

export function mSlam(id: string, name: string, o: { shape: Shape; coef: number; delay?: number; cd?: number; range?: [number, number]; anim?: string; telegraph?: 'red' | 'yellow' | 'blue' | 'purple'; anchor?: 'self' | 'target' | 'point'; reaction?: HitSpec['reaction']; status?: HitSpec['status']; vfx?: string }): SkillDef {
  const d = o.delay ?? 0.9;
  return skill({
    id, name, description: '', castType: 'normal', cooldown: o.cd ?? 8, mana: 0, kind: 'monster',
    tags: [`range:${(o.range ?? [0, 4]).join('-')}`],
    stages: [
      stage(o.anim ?? 'Slam', d + 0.5, [
        area(0, {
          shape: o.shape, anchor: o.anchor ?? 'self', delay: d, telegraph: o.telegraph ?? 'red', vfx: o.vfx,
          hit: { shape: o.shape, coef: o.coef, reaction: o.reaction ?? { kind: 'knockdown', duration: 1.2, power: 1 }, ...(o.status ? { status: o.status } : {}) },
        }),
      ]),
    ],
  });
}

export function mShoot(id: string, name: string, o: { coef: number; speed?: number; range?: number; windup?: number; cd?: number; count?: number; spread?: number; radius?: number; anim?: string; status?: HitSpec['status']; explode?: Shape; vfx?: string; hue?: number }): SkillDef {
  const w = o.windup ?? 0.55;
  const spec: ProjectileSpec = {
    speed: o.speed ?? 16, range: o.range ?? 14, radius: o.radius ?? 0.45, count: o.count, spreadDeg: o.spread, vfx: o.vfx,
    hit: { shape: { kind: 'circle', radius: o.radius ?? 0.45 }, coef: o.explode ? 0 : o.coef, ...(o.status ? { status: o.status } : {}), reaction: { kind: 'flinch' } },
    ...(o.explode ? { explode: { shape: o.explode, coef: o.coef, reaction: { kind: 'knockback', power: 1.5 } } } : {}),
  };
  return skill({
    id, name, description: '', castType: 'normal', cooldown: o.cd ?? 3, mana: 0, kind: 'monster', hue: o.hue,
    tags: [`range:${[0, (o.range ?? 14) - 1].join('-')}`],
    stages: [stage(o.anim ?? 'Attack', w + 0.4, [proj(w, spec)])],
  });
}

// ---------------------------------------------------------------------------
// Elite / boss builders. They share `MonsterSkillOpts`: `counter` opens a blue
// counter window, `announce` shows a banner on every peer (announce actions
// replicate with the cast), `superArmor` keeps elites and named monsters from
// being flinched out of their big moves. `range` is always the [lo, hi]
// distance band the melee/ranged brains use to pick the attack.

export type TelegraphColor = 'red' | 'yellow' | 'blue' | 'purple';

export interface MonsterSkillOpts {
  cd?: number;
  range?: [number, number];
  anim?: string;
  superArmor?: SuperArmor;
  element?: Element;
  hue?: number;
  /** Counter window as [start, duration] seconds into the stage (blue glow). */
  counter?: [number, number];
  /** Banner shown on every peer when the skill starts. */
  announce?: string;
  announceStyle?: 'mechanic' | 'warning' | 'boss';
  /** Clamps the aim point (leaps, lobbed attacks). */
  maxRange?: number;
}

/** Hit shape for visual-only markers. */
const NO_HIT: Shape = { kind: 'rect', length: 0.1, width: 0.1 };

/** Single-stage monster skill; the builders below are thin layers over it. */
export function mSkill(id: string, name: string, o: MonsterSkillOpts & { duration: number; actions: SkillAction[] }): SkillDef {
  const actions = [...o.actions];
  if (o.counter) actions.push(counterWindow(o.counter[0], o.counter[1]));
  if (o.announce) actions.push(announce(0, o.announce, o.announceStyle ?? 'mechanic'));
  return skill({
    id, name, description: '', castType: 'normal', cooldown: o.cd ?? 6, mana: 0, kind: 'monster',
    superArmor: o.superArmor ?? 'none',
    tags: [`range:${(o.range ?? [0, 4]).join('-')}`],
    ...(o.element ? { element: o.element } : {}),
    ...(o.hue !== undefined ? { hue: o.hue } : {}),
    ...(o.maxRange ? { maxRange: o.maxRange } : {}),
    stages: [stage(o.anim ?? 'Attack', o.duration, actions)],
  });
}

/** Ground marker without damage (e.g. a charge path whose damage follows the body). Vanishes if the cast is interrupted. */
export function marker(at: number, shape: Shape, delay: number, color: TelegraphColor = 'red', anchor: 'self' | 'point' = 'self'): SkillAction {
  return area(at, { shape, anchor, delay, telegraph: color, cancelOnInterrupt: true, hit: { shape: NO_HIT, coef: 0 } });
}

/** One delayed ground eruption (or persistent field) of a monster skill. */
export interface AreaStep {
  /** Spawn time within the stage (default 0: the telegraph shows at once). */
  at?: number;
  shape: Shape;
  /** Telegraph time before it erupts. */
  delay: number;
  coef: number;
  anchor?: 'self' | 'point';
  telegraph?: TelegraphColor;
  reaction?: HitSpec['reaction'];
  status?: StatusApply[];
  element?: Element;
  /** Persistent field: lifetime after the eruption and damage tick interval. */
  duration?: number;
  tick?: number;
  /** Pulls players toward the centre while active (m/s). */
  pull?: number;
  vfx?: string;
  /** Cleared if the caster is interrupted before it erupts (counters, stagger checks). */
  cancelOnInterrupt?: boolean;
}

export function areaStep(st: AreaStep): SkillAction {
  return area(st.at ?? 0, {
    shape: st.shape,
    anchor: st.anchor ?? 'self',
    delay: st.delay,
    telegraph: st.telegraph ?? 'red',
    ...(st.duration ? { duration: st.duration, tickInterval: st.tick ?? 1 } : {}),
    ...(st.pull ? { pull: st.pull } : {}),
    ...(st.vfx ? { vfx: st.vfx } : {}),
    ...(st.cancelOnInterrupt ? { cancelOnInterrupt: true } : {}),
    hit: {
      shape: st.shape,
      coef: st.coef,
      reaction: st.reaction ?? { kind: 'knockdown', duration: 1, power: 1 },
      ...(st.status ? { status: st.status } : {}),
      ...(st.element ? { element: st.element } : {}),
    },
  });
}

/** Telegraphed ground attacks: slams, donuts, in-out combos, lanes, pools. The caster holds the pose until the last eruption. */
export function mAreas(id: string, name: string, o: MonsterSkillOpts & { steps: AreaStep[]; recover?: number; extra?: SkillAction[] }): SkillDef {
  const end = Math.max(0, ...o.steps.map((s) => (s.at ?? 0) + s.delay));
  return mSkill(id, name, { ...o, anim: o.anim ?? 'Slam', duration: end + (o.recover ?? 0.45), actions: [...o.steps.map(areaStep), ...(o.extra ?? [])] });
}

export interface ComboHit {
  at: number;
  shape: Shape;
  coef: number;
  /** Steps forward this far just before the hit. */
  step?: number;
  reaction?: HitSpec['reaction'];
  status?: StatusApply[];
  vfx?: string;
}

/** Multi-strike melee string (readable first wind-up, no ground markers). */
export function mCombo(id: string, name: string, o: MonsterSkillOpts & { hits: ComboHit[]; recover?: number }): SkillDef {
  const actions: SkillAction[] = [];
  for (const h of o.hits) {
    if (h.step) actions.push(move(Math.max(0, h.at - 0.14), h.step, 0.14));
    actions.push(hit(h.at, h.shape, h.coef, { reaction: h.reaction ?? { kind: 'flinch' }, ...(h.status ? { status: h.status } : {}), ...(h.vfx ? { vfx: h.vfx } : {}) }));
  }
  const last = Math.max(0, ...o.hits.map((h) => h.at));
  return mSkill(id, name, { ...o, anim: o.anim ?? 'Attack2', range: o.range ?? [0, 2.6], duration: last + (o.recover ?? 0.45), actions });
}

/** Spinning multi-hit around the caster, with a red circle during the wind-up. */
export function mSpin(id: string, name: string, o: MonsterSkillOpts & { radius: number; count: number; interval: number; coef: number; windup?: number; reaction?: HitSpec['reaction']; status?: StatusApply[] }): SkillDef {
  const w = o.windup ?? 0.55;
  return mSkill(id, name, {
    ...o,
    anim: o.anim ?? 'Spin',
    range: o.range ?? [0, o.radius - 0.5],
    duration: w + o.count * o.interval + 0.35,
    actions: [
      marker(0, circle(o.radius), w),
      hits(w, o.count, o.interval, circle(o.radius), o.coef, { reaction: o.reaction ?? { kind: 'flinch' }, ...(o.status ? { status: o.status } : {}) }),
    ],
  });
}

/**
 * Charging rush along the aim line: red path marker, multi-hit body during the
 * dash. `counterable` opens the counter window over the back of the wind-up; a
 * counter interrupts the cast before the dash (and clears the marker).
 */
export function mCharge(id: string, name: string, o: MonsterSkillOpts & { distance: number; coef: number; width?: number; windup?: number; dashTime?: number; count?: number; reaction?: HitSpec['reaction']; counterable?: boolean }): SkillDef {
  const w = o.windup ?? 0.9;
  const dash = o.dashTime ?? Math.max(0.3, o.distance / 24);
  const width = o.width ?? 2;
  const n = o.count ?? 3;
  const counter: [number, number] | undefined = o.counter ?? (o.counterable ? [w * 0.35, w * 0.65] : undefined);
  return mSkill(id, name, {
    ...o,
    anim: o.anim ?? 'Charge',
    range: o.range ?? [3, o.distance],
    counter,
    duration: w + dash + 0.55,
    actions: [
      marker(0, rect(o.distance + 1.5, width), w),
      move(w, o.distance, dash),
      hits(w + 0.02, n, dash / n, rect(2, width, -0.5), o.coef, { reaction: o.reaction ?? { kind: 'knockdown', duration: 1, power: 2.5 } }),
    ],
  });
}

/** Leap onto the aim point (clamped to `maxDistance`) with a landing telegraph. */
export function mLeap(id: string, name: string, o: MonsterSkillOpts & { radius: number; coef: number; maxDistance?: number; land?: number; reaction?: HitSpec['reaction']; status?: StatusApply[]; telegraph?: TelegraphColor }): SkillDef {
  const land = o.land ?? 1;
  const maxD = o.maxDistance ?? 12;
  return mSkill(id, name, {
    ...o,
    anim: o.anim ?? 'Leap',
    maxRange: maxD,
    range: o.range ?? [3, maxD],
    duration: land + 0.55,
    actions: [
      areaStep({ shape: circle(o.radius), anchor: 'point', delay: land, coef: o.coef, telegraph: o.telegraph ?? 'red', reaction: o.reaction ?? { kind: 'knockdown', duration: 1.2, power: 1.5 }, ...(o.status ? { status: o.status } : {}) }),
      move(land * 0.3, maxD, land * 0.65, { toPoint: true, ignoreCollision: true }),
    ],
  });
}

/** Breath / sweeping cone: cone marker, then several ticks. */
export function mBreath(id: string, name: string, o: MonsterSkillOpts & { radius: number; angle: number; coef: number; count?: number; interval?: number; windup?: number; status?: StatusApply[]; reaction?: HitSpec['reaction']; telegraph?: TelegraphColor }): SkillDef {
  const w = o.windup ?? 0.8;
  const n = o.count ?? 4;
  const iv = o.interval ?? 0.25;
  const shape = cone(o.radius, o.angle);
  return mSkill(id, name, {
    ...o,
    anim: o.anim ?? 'Breath',
    range: o.range ?? [0, o.radius - 1],
    duration: w + n * iv + 0.4,
    actions: [marker(0, shape, w, o.telegraph ?? 'red'), hits(w, n, iv, shape, o.coef, { reaction: o.reaction ?? { kind: 'flinch' }, ...(o.status ? { status: o.status } : {}) })],
  });
}

/**
 * Projectile volleys: `waves` fans of `count` projectiles; `coef` is per
 * projectile. `reach` is the travel distance, `lane` (width) draws a marker
 * along every projectile path during the wind-up.
 */
export function mVolley(id: string, name: string, o: MonsterSkillOpts & { coef: number; waves?: number; interval?: number; count?: number; spread?: number; speed?: number; reach?: number; radius?: number; windup?: number; status?: StatusApply[]; reaction?: HitSpec['reaction']; pierce?: boolean; explode?: Shape; vfx?: string; lane?: number }): SkillDef {
  const w = o.windup ?? 0.6;
  const waves = o.waves ?? 1;
  const iv = o.interval ?? 0.3;
  const r = o.radius ?? 0.45;
  const reach = o.reach ?? 16;
  const st = o.status ? { status: o.status } : {};
  const spec: ProjectileSpec = {
    speed: o.speed ?? 16,
    range: reach,
    radius: r,
    ...(o.count ? { count: o.count } : {}),
    ...(o.spread ? { spreadDeg: o.spread } : {}),
    ...(o.pierce ? { pierce: true } : {}),
    ...(o.vfx ? { vfx: o.vfx } : {}),
    hit: { shape: circle(r), coef: o.explode ? 0 : o.coef, reaction: o.reaction ?? { kind: 'flinch' }, ...(o.explode ? {} : st) },
    ...(o.explode ? { explode: { shape: o.explode, coef: o.coef, reaction: o.reaction ?? { kind: 'knockback', power: 1.5 }, ...st } } : {}),
  };
  const actions: SkillAction[] = [];
  for (let i = 0; i < waves; i++) actions.push(proj(w + i * iv, spec));
  if (o.lane) {
    const n = o.count ?? 1;
    const spread = o.spread ?? 0;
    for (let i = 0; i < n; i++) actions.push(marker(0, rect(reach, o.lane, 0, n > 1 ? -spread / 2 + (spread * i) / (n - 1) : 0), w));
  }
  return mSkill(id, name, { ...o, anim: o.anim ?? 'Shoot', range: o.range ?? [0, reach - 1], duration: w + (waves - 1) * iv + 0.45, actions });
}

/** Friendly pulse around the caster: heals, shields and/or buffs nearby allies. `duration` leaves a ticking field (totems). */
export function mSupport(id: string, name: string, o: MonsterSkillOpts & { radius: number; windup?: number; healPct?: number; shieldPct?: number; shieldDuration?: number; status?: StatusApply[]; duration?: number; tick?: number; vfx?: string }): SkillDef {
  const w = o.windup ?? 0.6;
  const shape = circle(o.radius);
  const fx: HitSpec = { shape, coef: 0, friendly: true };
  if (o.healPct) fx.healPct = o.healPct;
  if (o.shieldPct) {
    fx.shieldPct = o.shieldPct;
    fx.shieldDuration = o.shieldDuration ?? 5;
  }
  if (o.status) fx.status = o.status;
  return mSkill(id, name, {
    ...o,
    anim: o.anim ?? 'Cast',
    range: o.range ?? [0, 14],
    duration: w + 0.5,
    actions: [area(w, { shape, anchor: 'self', delay: 0, telegraph: 'none', ...(o.duration ? { duration: o.duration, tickInterval: o.tick ?? 1 } : {}), ...(o.vfx ? { vfx: o.vfx } : {}), hit: fx })],
  });
}

/** Roar: banner plus an optional nova (phase changes, summons, howls). Full super armor. */
export function mRoar(id: string, name: string, o: MonsterSkillOpts & { duration?: number; nova?: AreaStep }): SkillDef {
  return mSkill(id, name, { ...o, anim: o.anim ?? 'Roar', superArmor: o.superArmor ?? 'full', range: o.range ?? [0, 99], duration: o.duration ?? 1.4, actions: o.nova ? [areaStep(o.nova)] : [] });
}

/**
 * Stagger check: a long channel ending in a lethal arena-wide blast (purple).
 * The blast is a pending area flagged `cancelOnInterrupt`, so emptying the
 * stagger bar (World.applyStagger interrupts the cast) clears it on every
 * peer. The stage outlasts the delay even at the +40% attack-speed cap so the
 * cast is still running when the blast lands; scripts may end it early.
 */
export function mStaggerCheck(id: string, name: string, o: MonsterSkillOpts & { channel: number; radius: number; coef: number; text: string; status?: StatusApply[] }): SkillDef {
  return mSkill(id, name, {
    ...o,
    cd: o.cd ?? 60,
    range: o.range ?? [0, 99],
    anim: o.anim ?? 'Channel',
    superArmor: 'full',
    announce: o.text,
    announceStyle: 'warning',
    duration: o.channel * 1.45 + 0.5,
    actions: [
      areaStep({ at: 0.05, shape: circle(o.radius), delay: o.channel, coef: o.coef, telegraph: 'purple', reaction: { kind: 'knockdown', duration: 2, power: 3 }, vfx: 'pillar', cancelOnInterrupt: true, ...(o.status ? { status: o.status } : {}) }),
    ],
  });
}
